import React, { useEffect, useState } from 'react';
import _isEqual from 'lodash-es/isEqual';
import Button from 'components/Buttons/Button';
import { hasSimpleMapping } from 'components/EntitiesSelectorModals/ClassroomsMappingModal/hasSimpleMappping';
import 'twin.macro';
import {
  ClassroomMapping,
  ClassroomsMapping,
  NO_COPY,
} from 'components/EntitiesSelectorModals/ClassroomsMappingModal/ClassroomsMapping';
import Popover from 'components/Popover';
import {
  ClipboardDocumentIcon as ClipboardCopy,
  XMarkIcon as X,
} from '@heroicons/react/24/outline';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { Classroom } from 'types/classroom';
import useCopyEDT from 'queries/edt/useCopyEDT';
import { useNavigate } from 'react-router-dom';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { Trans, useTranslation } from 'react-i18next';
import { mobileHidden } from 'i18n/components';

interface Props {
  edtId: string;
  srcClassrooms: Classroom[];
  srcSchoolyearId: string;
  srcSchoolId: string;
}

export function SubmitButton(props: Props) {
  const { srcClassrooms, edtId, srcSchoolId, srcSchoolyearId } = props;
  const { t } = useTranslation(['emploiDuTemps', 'common']);

  const navigate = useNavigate();
  const { schoolyear, school } = useSchoolSchoolyear();
  const destClassrooms = useClassrooms();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const copyEDT = useCopyEDT();
  const [classroomMapping, setClassroomMapping] = useState<ClassroomMapping[]>(
    [],
  );
  useEffect(() => {
    setClassroomMapping(
      (srcClassrooms || []).map((c, i) => {
        if (!destClassrooms || destClassrooms.length < i + 1) {
          return {
            from: c.id,
            to: NO_COPY,
          };
        }
        return {
          from: c.id,
          to:
            (destClassrooms || []).length > i ? destClassrooms[i].id : NO_COPY,
        };
      }),
    );
  }, [JSON.stringify(destClassrooms), JSON.stringify(srcClassrooms)]);
  const submitLabel = (
    <span>
      <Trans
        i18nKey="emploiDuTemps:new.copyThisEdt"
        components={{ mobileHidden }}
      />
    </span>
  );
  const onSubmit = () =>
    copyEDT({
      srcSchoolyearId,
      srcSchoolId,
      destSchoolyearId: schoolyear.id,
      destSchoolId: school.id,
      mappings: (classroomMapping || []).filter(c => c?.to !== NO_COPY),
      edtId,
    }).then(({ edt }) => {
      navigate(
        `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/emploi-du-temps/${edt.id}`,
      );
    });

  if (
    !hasSimpleMapping(srcClassrooms || [], destClassrooms || []) &&
    !_isEqual(srcClassrooms, destClassrooms)
  ) {
    return (
      <Popover
        placement="top"
        portal
        open={isPopoverOpen}
        offsetInPx={10}
        onClickOutside={() => {}}
      >
        <Popover.Button
          extralarge
          intent="primary"
          rightIcon={ClipboardCopy}
          onClick={() => {
            if (!isPopoverOpen) {
              setIsPopoverOpen(true);
              return;
            }
            return onSubmit();
          }}
        >
          {isPopoverOpen ? t('common:buttons.validate') : submitLabel}
        </Popover.Button>
        <Popover.Panel size="xl" tw="relative">
          <div tw="p-4">
            <div tw="mb-4">{t('common:classroomMapping.title')}</div>
            <div tw="mb-4" data-testid="classroomMapping">
              <ClassroomsMapping
                srcClassrooms={srcClassrooms || []}
                destClassrooms={destClassrooms || []}
                value={classroomMapping}
                onChange={setClassroomMapping}
              />
            </div>
            <div tw="absolute top-1 right-1 ">
              <Button
                minimal
                icon={X}
                onClick={() => setIsPopoverOpen(false)}
              />
            </div>
          </div>
        </Popover.Panel>
      </Popover>
    );
  }
  return (
    <Button
      large
      intent="primary"
      rightIcon={ClipboardCopy}
      onClick={() => onSubmit()}
    >
      {submitLabel}
    </Button>
  );
}
